/**
 * Coherence Engine — evaluates registry rules against resolved data (MCP sync).
 * BET-99 / ARCH-16 / ARCH-19
 *
 * ⚠ SYNCED from src/lib/coherence/engine.ts — do not edit here.
 * Run `npm run sync:coherence` to refresh.
 */

import type {
  CoherenceReport,
  CoherenceRule,
  CoherenceSnapshot,
  CoherenceViolation,
  ExpectedCoverage,
  RegistryRef,
  ResolvedRegistries,
  RuleSeverity,
  RuleType,
} from './types.js';

const MAX_VIOLATIONS = 50; // RH6: cap to prevent snapshot bloat

const SEVERITY_RANK: Record<RuleSeverity, number> = { error: 0, warning: 1, info: 2 };

function severityFromCoverage(coverage: ExpectedCoverage): RuleSeverity {
  switch (coverage) {
    case 'all':
      return 'error';
    case 'curated':
      return 'warning';
    case 'subset':
      return 'info';
  }
}

type EvaluatorArgs = {
  rule: CoherenceRule;
  subjectRef: RegistryRef;
  subjectKeys: ReadonlySet<string>;
  referenceRef: RegistryRef;
  referenceKeys: ReadonlySet<string>;
};

function evaluateCoverage(args: EvaluatorArgs): CoherenceViolation[] {
  const { rule, subjectRef, subjectKeys, referenceKeys } = args;
  const severity = rule.severityOverride ?? severityFromCoverage(subjectRef.expectedCoverage);
  const violations: CoherenceViolation[] = [];

  for (const slug of referenceKeys) {
    if (!subjectKeys.has(slug)) {
      violations.push({
        ruleId: rule.id,
        ruleType: 'coverage',
        registryId: rule.subject,
        collectionSlug: slug,
        message: `"${slug}" missing from ${subjectRef.exportName} (${subjectRef.path})`,
        severity,
        fix: `Add "${slug}" to ${subjectRef.exportName} in ${subjectRef.path}`,
      });
    }
  }

  return violations;
}

function evaluateSync(args: EvaluatorArgs): CoherenceViolation[] {
  const { rule, subjectRef, subjectKeys, referenceRef, referenceKeys } = args;
  const violations: CoherenceViolation[] = [];

  for (const slug of referenceKeys) {
    if (!subjectKeys.has(slug)) {
      violations.push({
        ruleId: rule.id,
        ruleType: 'sync',
        registryId: rule.subject,
        collectionSlug: slug,
        message: `"${slug}" in ${referenceRef.exportName} but missing from ${subjectRef.exportName}`,
        severity: 'error',
        fix: `Add "${slug}" to ${subjectRef.exportName} in ${subjectRef.path}`,
      });
    }
  }

  for (const slug of subjectKeys) {
    if (!referenceKeys.has(slug)) {
      violations.push({
        ruleId: rule.id,
        ruleType: 'sync',
        registryId: referenceRef.id,
        collectionSlug: slug,
        message: `"${slug}" in ${subjectRef.exportName} but missing from ${referenceRef.exportName}`,
        severity: 'error',
        fix: `Add "${slug}" to ${referenceRef.exportName} in ${referenceRef.path}`,
      });
    }
  }

  return violations;
}

function evaluateSubset(args: EvaluatorArgs): CoherenceViolation[] {
  const { rule, subjectRef, subjectKeys, referenceRef, referenceKeys } = args;
  const severity = rule.severityOverride ?? severityFromCoverage(subjectRef.expectedCoverage);
  const violations: CoherenceViolation[] = [];

  for (const slug of subjectKeys) {
    if (!referenceKeys.has(slug)) {
      violations.push({
        ruleId: rule.id,
        ruleType: 'subset',
        registryId: rule.subject,
        collectionSlug: slug,
        message: `"${slug}" in ${subjectRef.exportName} is not a classified collection`,
        severity,
        fix: `Remove "${slug}" from ${subjectRef.exportName}, or add it to ${referenceRef.exportName}`,
      });
    }
  }

  return violations;
}

function evaluateImplication(args: EvaluatorArgs): CoherenceViolation[] {
  const { rule, subjectRef, subjectKeys, referenceRef, referenceKeys } = args;
  const severity = rule.severityOverride ?? severityFromCoverage(referenceRef.expectedCoverage);
  const violations: CoherenceViolation[] = [];

  for (const slug of subjectKeys) {
    if (!referenceKeys.has(slug)) {
      violations.push({
        ruleId: rule.id,
        ruleType: 'implication',
        registryId: referenceRef.id,
        collectionSlug: slug,
        message: `"${slug}" has ${subjectRef.description} but no ${referenceRef.description}`,
        severity,
        fix: `Add "${slug}" to ${referenceRef.exportName} in ${referenceRef.path}`,
      });
    }
  }

  return violations;
}

const EVALUATORS: Record<RuleType, (args: EvaluatorArgs) => CoherenceViolation[]> = {
  coverage: evaluateCoverage,
  sync: evaluateSync,
  subset: evaluateSubset,
  implication: evaluateImplication,
};

/**
 * Evaluate coherence rules against resolved registry data.
 * Returns a structured report suitable for any renderer.
 *
 * Pass `checkedAt` explicitly for deterministic tests; defaults to Date.now().
 */
export function evaluateCoherence(
  manifest: readonly RegistryRef[],
  rules: readonly CoherenceRule[],
  resolved: ResolvedRegistries,
  checkedAt?: number,
): CoherenceReport {
  const registryMap = new Map(manifest.map((r) => [r.id, r]));
  const allViolations: CoherenceViolation[] = [];

  for (const rule of rules) {
    const subjectRef = registryMap.get(rule.subject);
    if (!subjectRef) continue;

    const subjectKeys = resolved[rule.subject];
    if (!subjectKeys) continue;

    if (!rule.reference) continue;
    const referenceRef = registryMap.get(rule.reference);
    if (!referenceRef) continue;
    const referenceKeys = resolved[rule.reference];
    if (!referenceKeys) continue;

    allViolations.push(
      ...EVALUATORS[rule.type]({ rule, subjectRef, subjectKeys, referenceRef, referenceKeys }),
    );
  }

  allViolations.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  const capped = allViolations.slice(0, MAX_VIOLATIONS);

  return {
    checkedAt: checkedAt ?? Date.now(),
    totalRegistries: manifest.length,
    totalRules: rules.length,
    violations: capped,
    summary: {
      errors: capped.filter((v) => v.severity === 'error').length,
      warnings: capped.filter((v) => v.severity === 'warning').length,
      infos: capped.filter((v) => v.severity === 'info').length,
      total: capped.length,
    },
  };
}

/** Compress a full report into a compact snapshot for session persistence. */
export function toSnapshot(report: CoherenceReport, maxTopGaps = 10): CoherenceSnapshot {
  const topGaps = report.violations
    .filter((v) => v.severity !== 'info')
    .slice(0, maxTopGaps)
    .map((v) => ({
      registry: v.registryId,
      slug: v.collectionSlug,
      severity: v.severity,
    }));

  return {
    checkedAt: report.checkedAt,
    totalGaps: report.summary.total,
    topGaps,
  };
}
